import React from 'react';
import { ArrowRight, Check, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { useContent } from '../context/ContentContext';
import { useLang } from '../context/LanguageContext';
import Reveal from './Reveal';

const FALLBACK = {
  eyebrow: 'Web-Apps & Automationen für Schweizer KMU',
  title_part1: 'Weniger Büroarbeit.',
  title_part2: 'Mehr Zeit für Ihre Kunden.',
  subtitle_line1: 'Wir bauen Ihre Website und digitalisieren Ihre Abläufe: Online-Buchungen, automatische Offerten, Kundenverwaltung.',
  subtitle_line2: 'Alles aus einer Hand – Sie müssen nichts davon verstehen.',
  cta_primary_label: 'Beratung anfragen',
  cta_secondary_label: 'Referenzen ansehen',
  stats: [
    { value: 'Ab 500 CHF', label: 'einmalig' },
    { value: 'Ab 150 CHF', label: 'pro Monat' },
    { value: '100%', label: 'individuell' },
  ],
  customers_label: 'Unsere Kunden',
  customers: [],
};

const Hero = () => {
  const data = useContent('hero', FALLBACK);
  const { t } = useLang();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="bg-white pt-28 lg:pt-32 pb-16 px-6 overflow-hidden">
      <div className="max-w-[1080px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left – Text */}
          <Reveal className="text-left">
            {data.eyebrow && (
              <p className="text-xs font-semibold text-[#0071E3] tracking-widest uppercase mb-5" data-testid="hero-eyebrow">
                {data.eyebrow}
              </p>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1D1D1F] leading-tight tracking-tight mb-6" data-testid="hero-title">
              {data.title_part1}
              <br />
              <span className="text-[#86868B]">{data.title_part2}</span>
            </h1>

            <p className="text-lg md:text-xl text-[#86868B] leading-relaxed mb-8" data-testid="hero-subtitle">
              {data.subtitle_line1}
              <br />
              {data.subtitle_line2}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                size="lg"
                onClick={() => scrollToSection('contact')}
                className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full px-8 py-6 text-base h-12 group"
                data-testid="hero-cta-primary"
              >
                {data.cta_primary_label}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('projects')}
                className="border border-[#0071E3] text-[#0071E3] hover:bg-[#0071E3] hover:text-white rounded-full px-8 py-6 text-base h-12"
                data-testid="hero-cta-secondary"
              >
                {data.cta_secondary_label}
              </Button>
            </div>

            <div className="flex flex-wrap gap-3">
              {(data.stats || []).map((stat, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 bg-[#F5F5F7] text-[#1D1D1F] text-sm font-medium rounded-full px-4 py-2"
                  data-testid={`hero-stat-${idx}`}
                >
                  <Check className="w-4 h-4 text-[#0071E3]" strokeWidth={2.5} />
                  {stat.value} {stat.label}
                </span>
              ))}
            </div>
          </Reveal>

          {/* Right – Screenshot collage (kompakt auf Mobile) */}
          <Reveal delay={150}>
            <div className="relative mt-4 lg:mt-0 pb-12" data-testid="hero-collage">
              <div className="rounded-3xl overflow-hidden border border-[#D2D2D7] shadow-2xl bg-white ml-8 lg:ml-10">
                <img
                  src="/screenshots/bellastrada.jpg"
                  alt="Online-Buchungssystem BellaStrada"
                  className="w-full h-52 sm:h-72 lg:h-[360px] object-cover object-top"
                />
              </div>

              <div className="absolute bottom-0 -left-1 lg:-left-2 w-40 sm:w-52 lg:w-60 rounded-2xl overflow-hidden border border-[#D2D2D7] shadow-xl bg-white">
                <img
                  src="/screenshots/strongfit-app.jpg"
                  alt="Studio-Management StrongFit"
                  className="w-full h-24 sm:h-32 lg:h-36 object-cover object-top"
                />
              </div>

              <div className="absolute top-4 -left-2 lg:-left-6 bg-white rounded-full shadow-lg border border-[#D2D2D7]/60 px-3 lg:px-4 py-2 lg:py-2.5 flex items-center gap-2" data-testid="hero-badge-booking">
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" strokeWidth={2} />
                <span className="text-[10px] sm:text-xs font-medium text-[#1D1D1F]">{t('hero.badgeBooking')}</span>
              </div>

              <div className="absolute bottom-8 right-1 lg:right-4 bg-white rounded-full shadow-lg border border-[#D2D2D7]/60 px-3 lg:px-4 py-2 lg:py-2.5 flex items-center gap-2" data-testid="hero-badge-offer">
                <Sparkles className="w-4 h-4 text-[#0071E3] flex-shrink-0" strokeWidth={2} />
                <span className="text-[10px] sm:text-xs font-medium text-[#1D1D1F]">{t('hero.badgeOffer')}</span>
              </div>
            </div>
          </Reveal>
        </div>

        {(data.customers || []).length > 0 && (
          <Reveal>
            <div className="pt-10 mt-12 lg:mt-20 border-t border-[#D2D2D7] text-center" data-testid="hero-customers-bar">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#86868B] mb-5">
                {data.customers_label || 'Unsere Kunden'}
              </p>
              <div className="flex flex-wrap justify-center items-center gap-x-10 gap-y-4">
                {data.customers.map((name, idx) => (
                  <button
                    key={idx}
                    onClick={() => scrollToSection('projects')}
                    className="text-lg md:text-xl font-semibold text-[#86868B]/70 hover:text-[#1D1D1F] transition-colors tracking-tight"
                    data-testid={`hero-customer-${idx}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default Hero;
